import type { Toast, ToastVariant } from '@helpers/types/Toast';
import { useToastStore } from '@stores/toastStore';
import { ColorVariants } from '@therame/design-tokens/src/color-variants';
import type { GenericPopUpBannerProps, MingcuteIconName } from '@therame/ui';

/** Semantic toast variant → design-system colour variant (drives the icon-chip colour). */
const TOAST_VARIANT: Record<ToastVariant, ColorVariants> = {
  error: ColorVariants.ERROR,
  warning: ColorVariants.WARNING,
  info: ColorVariants.INFO,
  success: ColorVariants.ACTIVE,
};

/** Icon inferred per variant when the toast doesn't override it. */
const DEFAULT_ICON: Record<ToastVariant, MingcuteIconName> = {
  error: 'icon-[mingcute--alert-fill]',
  warning: 'icon-[mingcute--warning-fill]',
  info: 'icon-[mingcute--information-fill]',
  success: 'icon-[mingcute--check-line]',
};

/**
 * Maps a toast onto GenericPopUpBanner props — the toast counterpart of
 * useNotificationBanner. Keeps all toast-specific knowledge out of the banner.
 */
export const useToastBanner = () => {
  const toastStore = useToastStore();

  const toastBannerProps = (toast: Toast): GenericPopUpBannerProps => {
    const type = toast.details.type ?? 'success';
    const action = toast.details.buttonAction;

    return {
      variant: TOAST_VARIANT[type],
      icon: toast.details.icon ?? DEFAULT_ICON[type],
      contentText: toast.details.text,
      autoCloseMs: toast.autoCloseMs,
      buttons: action
        ? [
            {
              text: toast.details.buttonText ?? '',
              icon: toast.details.buttonIcon,
              colorVariant: ColorVariants.MOST_IMPORTANT,
              onClick: () => {
                action();
                toastStore.dismissToast(toast.id);
              },
            },
          ]
        : undefined,
    };
  };

  return { toastBannerProps };
};
